import { useCallback, useEffect, useRef } from 'react';

type Params = {
  // The scroll container whose overflow decides whether more history is
  // needed, and the content inside it. Both are watched for resizes.
  scrollNode: HTMLElement | null;
  contentNode: HTMLElement | null;
  // Whether another older page can be requested right now: there is one to
  // request, none is in flight, and the last one didn't fail.
  enabled: boolean;
  // True while the caller is restoring a scroll position itself (a page being
  // prepended). Its metrics are captured before the load and consumed after,
  // so starting a second load inside that window would lose them.
  isPaused?: () => boolean;
  // Request the next older page. Receives the scroller so the caller can
  // capture its pre-prepend metrics from the same measurement this hook read.
  onFill: (scroller: HTMLElement) => void;
};

// Keeps a paginated transcript scrollable.
//
// A near-top scroll trigger is the usual way to page in history, and it is
// unreachable whenever the loaded newest page FITS the viewport: no overflow
// means no scrollbar, and no wheel or touch input can move a scroller with
// nothing to scroll, so no scroll event ever fires and the rest of the
// conversation stays permanently out of reach (DAT-1404 — see
// AI_ASSISTANT_TRANSCRIPT_SCROLL_ISSUE.md). This closes that hole by pulling
// older pages until the scroller actually overflows.
//
// The trigger is a ResizeObserver on the same two nodes bottom-follow watches,
// because "does this overflow" is a question about geometry, and geometry is
// what changes here: a page landing, a streamed delta, the composer growing
// and shrinking the viewport under it, the window being enlarged (the
// "increase the window height and it breaks again" half of the report). A
// render-keyed effect would miss the ones React does not re-render this
// component for — most visibly the composer, which owns its own auto-grow
// height — and would run on every commit to catch the ones it does.
export default function useFillViewportWithOlderPages(params: Params) {
  const { scrollNode, contentNode, enabled, isPaused, onFill } = params;

  // Mirrored so the observer subscribes to the nodes alone: re-observing on
  // every render would re-fire the initial notification each time.
  const scrollNodeRef = useRef(scrollNode);
  scrollNodeRef.current = scrollNode;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;
  const onFillRef = useRef(onFill);
  onFillRef.current = onFill;

  const check = useCallback(() => {
    const list = scrollNodeRef.current;
    if (!list || !enabledRef.current || isPausedRef.current?.()) return;
    // Already scrollable: the scroll-driven trigger takes it from here.
    if (list.scrollHeight > list.clientHeight) return;
    onFillRef.current(list);
  }, []);

  useEffect(() => {
    if (!scrollNode) return undefined;
    const observer = new ResizeObserver(check);
    // Observing fires an initial notification, which is the mount check: a
    // conversation whose first page fits is in the deadlock from the start.
    observer.observe(scrollNode);
    if (contentNode) observer.observe(contentNode);
    return () => observer.disconnect();
  }, [scrollNode, contentNode, check]);

  // A landed page re-enables the hook (the in-flight flag clears) and normally
  // resizes the content too, but not always — a page of rows the transcript
  // does not render adds no height at all. Re-checking on the flip keeps the
  // loop moving through those instead of stalling one page short.
  useEffect(() => {
    if (enabled) check();
  }, [enabled, check]);
}
